import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@mlm/ui';

/**
 * "Decide, and say why" — the shape every reject / resolve / dismiss in the
 * review queues shares.
 *
 * It replaces `window.prompt('Reason (optional)')`, which had three problems
 * worth a component: the browser prompt is a single line (a reason worth
 * reading is usually two sentences), it renders unthemed and unlabelled so the
 * reviewer cannot see WHAT they are rejecting while they type, and it is
 * synchronous — the mutation fired from inside an event handler with no
 * pending state, so a double-press sent the decision twice.
 *
 * The note stays OPTIONAL here because it is optional on every route that
 * consumes it; a required-note variant should gate `onConfirm`, not the field.
 */
export function ReviewDecisionDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  confirmLabel,
  confirmVariant = 'destructive',
  onConfirm,
  pending = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What is being decided — the reviewer should not have to remember. */
  description?: React.ReactNode;
  label: string;
  placeholder?: string;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive';
  onConfirm: (note: string) => void;
  pending?: boolean;
}) {
  const [note, setNote] = useState('');

  // Clear on CLOSE rather than on open: a reason typed against one course must
  // never be carried into the next one the reviewer opens.
  useEffect(() => {
    if (!open) setNote('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription asChild>
              <div>{description}</div>
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground">{label}</label>
          <Textarea
            rows={3}
            value={note}
            placeholder={placeholder}
            disabled={pending}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            disabled={pending}
            onClick={() => onConfirm(note.trim())}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
